<!--
  `card`: the two-column design (heading + instructions on top, «Tu entrega» beside «Recursos de la
  tarea»). One card and nothing around it — the background it sits on is the host's. Everything below
  `#accordionLayout` is the production accordion, untouched, for `inline` and `modal`.
-->
<ng-container *ngIf="variant === 'card'; else accordionLayout">
  <article class="task-card" [class.task-card--bleed]="bleed">
    <!-- cargando -->
    <div *ngIf="phase === 'loading'" class="task-card__head flex flex-col gap-4">
      <div class="flex gap-2">
        <ion-skeleton-text [animated]="true" class="w-16 h-6"></ion-skeleton-text>
        <ion-skeleton-text [animated]="true" class="w-20 h-6"></ion-skeleton-text>
      </div>
      <ion-skeleton-text [animated]="true" class="w-9/12 h-8"></ion-skeleton-text>
      <ion-skeleton-text [animated]="true" class="w-full h-4"></ion-skeleton-text>
      <ion-skeleton-text [animated]="true" class="w-10/12 h-4"></ion-skeleton-text>
      <ion-skeleton-text [animated]="true" class="w-full h-32 mt-4"></ion-skeleton-text>
    </div>

    <!-- la tarea no pudo cargarse -->
    <div *ngIf="phase === 'error'" class="task-card__head flex flex-col items-center gap-3 text-center">
      <p class="text-base text-gray-700">{{ errorMsg }}</p>
      <ion-button fill="outline" size="small" (click)="load()">Reintentar</ion-button>
    </div>

    <ng-container *ngIf="task && phase !== 'loading' && phase !== 'error'">
      <header class="task-card__head">
        <div *ngIf="typeLabel || statusChip" class="flex flex-wrap items-center gap-2 mb-2">
          <!-- <span *ngIf="typeLabel" class="task-chip task-chip--type">{{ typeLabel }}</span> -->
          <span *ngIf="statusChip" class="task-chip">{{ statusChip }}</span>
        </div>
        <h2 *ngIf="task?.title" class="task-card__title">{{ task.title }}</h2>
        <p class="task-card__indications">
          <strong class="font-bold">Indicaciones:</strong>
          {{ task.indications || 'Aquí encontrarás todos los recursos para realizar y subir tu tarea.' }}
        </p>
      </header>

      <div class="task-card__body" [class.task-card__body--single]="!task.project_resources?.length">
        <!-- Recursos de la tarea: sin recursos, la sección no se muestra y la entrega ocupa todo el ancho -->
        <aside *ngIf="task.project_resources?.length" class="task-card__resources py-3 px-4 lg:p-5 rounded-l-lg">
          <div class="flex items-baseline justify-between gap-3 mb-3">
            <h3 class="task-card__heading text-base text-gray_text">Recursos de la tarea</h3>
            <span *ngIf="task.project_resources?.length as total" class="task-card__count">
              {{ total }} {{ total === 1 ? 'archivo' : 'archivos' }}
            </span>
          </div>

          <ul class="flex flex-col gap-3">
            <li *ngFor="let resource of task.project_resources" class="task-file task-file--resource">
              <span class="task-badge" [ngClass]="kindOf(resource).tone">{{ kindOf(resource).tag }}</span>
              <div class="min-w-0 flex-1">
                <p class="task-file__name text-sm overflow-text-cover-2">{{ resource.title }}</p>
                <p class="task-file__meta">
                  {{ kindOf(resource).description }}<ng-container *ngIf="fileSize(resource) as size"> · {{ size }}</ng-container>
                </p>
              </div>
              <button type="button" class="task-link" (click)="confirmDownload(resource)"
                [attr.aria-label]="'Descargar ' + resource.title">Descargar</button>
            </li>
          </ul>
        </aside>

        <!-- Tu entrega -->
        <section class="task-card__delivery py-3 px-4 lg:py-5 lg:px-6 bg-white"
          [ngClass]="task.project_resources?.length ? 'rounded-r-lg' : 'rounded-lg'">
          <div class="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1 mb-3">
            <h3 class="task-card__heading text-base text-gray_text">Tu entrega</h3>
            <p *ngIf="deliveryHint" class="task-card__hint">{{ deliveryHint }}</p>
          </div>

          <div *ngIf="errorMsg || successMsg || (isDelivered && !successMsg)"
          class="items-center gap-3 mt-4 rounded-lg bg-primary_medium_light_variant  p-3 grid grid-cols-[auto_1fr]"
          [ngClass]="{'bg-red-200': errorMsg}"
          >
            <div class="rounded-full bg-primary flex items-center justify-center w-8 h-8" >
              <mat-icon class="mdi mdi-file-clock text-white text-2xl" ></mat-icon>
            </div>
            <p *ngIf="errorMsg" class="text-sm text-red-600 text-left" role="alert">{{ errorMsg }}</p>
            <p *ngIf="successMsg" class="text-sm text-primary font-medium text-left" role="status">{{ successMsg }}</p>
            <p *ngIf="isDelivered && !successMsg" class="text-sm text-gray-600 text-left flex flex-col">
              <span class="font-bold">En revisión</span>
              Ya enviaste tu tarea.
            </p>
          </div>

          <!-- lo que dejó quien la revisó -->
          <div *ngIf="task.msg_to_user && !isObserved" class="task-feedback mb-4">
            <p class="font-bold mb-1">Comentarios</p>
            <p class="whitespace-pre-line">{{ task.msg_to_user }}</p>
          </div>

          <!-- observada: el aviso, lo que pidió quien la revisó y lo ya cargado, juntos en un panel -->
          <div *ngIf="isObserved" class="task-observed">
            <div class="task-observed__banner">
              <span class="task-observed__icon" aria-hidden="true">!</span>
              <div class="min-w-0">
                <p class="task-observed__title">Tu supervisor pidió cambios</p>
                <p class="task-observed__subtitle">Corrige lo indicado y vuelve a enviar tu tarea.</p>
              </div>
            </div>
            <p *ngIf="task.msg_to_user" class="task-observed__note">{{ task.msg_to_user }}</p>
            <ng-container *ngTemplateOutlet="filesList"></ng-container>
          </div>

          <h3 *ngIf="isObserved && canUpload" class="task-card__heading text-base text-gray_text pt-2 mb-3">Enviar cambios</h3>

          <ng-container *ngIf="canUpload && !isDelivered">
            <div class="task-dropzone" role="button" tabindex="0"
              [class.task-dropzone--compact]="isObserved"
              [class.is-dragging]="dragging" [class.is-disabled]="!dropzoneEnabled"
              [attr.aria-disabled]="!dropzoneEnabled"
              (click)="dropzoneEnabled && fileInput.click()"
              (keydown.enter)="dropzoneEnabled && fileInput.click()"
              (keydown.space)="$event.preventDefault(); dropzoneEnabled && fileInput.click()"
              (dragover)="onDragOver($event)" (dragleave)="onDragLeave($event)" (drop)="onDrop($event)">
              <span class="task-dropzone__icon" aria-hidden="true">
                <mat-icon class="mdi mdi-arrow-up"></mat-icon>
              </span>
              <div class="flex flex-col">
                <p class="task-dropzone__title">
                  <ng-container *ngIf="dropzoneEnabled; else dropzoneClosed">
                    <span class="hidden lg:inline">Arrastra tus archivos o haz clic para subirlos</span>
                    <span class="lg:hidden">Toca para subir tus archivos</span>
                  </ng-container>
                  <ng-template #dropzoneClosed>{{ isSending ? 'Enviando tus archivos…' : 'Llegaste al máximo de archivos' }}</ng-template>
                </p>
                <p class="task-dropzone__rules">{{ dropzoneRules }}</p>
              </div>
            </div>
            <input #fileInput type="file" multiple class="hidden" [accept]="acceptTypes" (change)="onFileSelected($event)" />
          </ng-container>

          <ng-container *ngIf="!isObserved">
            <ng-container *ngTemplateOutlet="filesList"></ng-container>
          </ng-container>

          <ng-template #filesList>
          <ul *ngIf="userResources.length" class="flex flex-col gap-2 mt-3">
            <li *ngFor="let resource of userResources; let i = index" class="task-file">
              <span class="task-badge" [ngClass]="kindOf(resource).tone">{{ kindOf(resource).tag }}</span>
              <div class="min-w-0 flex-1">
                <p class="task-file__name text-sm">{{ resourceName(resource) }}</p>
                <p class="task-file__meta">
                  <ng-container *ngIf="fileSize(resource) as size">{{ size }} · </ng-container>
                  <span class="task-file__state" [class.is-done]="isStored(resource) || isSending">
                    {{ isStored(resource) ? 'Cargado' : isSending ? 'Cargando' : 'Por enviar' }}
                  </span>
                </p>
              </div>
              <button *ngIf="!isDelivered" type="button" class="task-file__remove" [disabled]="isSending"
                (click)="removeResource(i)" aria-label="Quitar archivo">
                <mat-icon class="mdi mdi-delete"></mat-icon>
              </button>
            </li>
          </ul>
          </ng-template>

          <div class="flex flex-col items-center gap-3 mt-5">
            <button *ngIf="canUpload && !isDelivered" type="button" class="task-send" [disabled]="!canSend" (click)="send()">
              <ion-spinner *ngIf="isSending" name="crescent"></ion-spinner>
              <span>{{ isSending ? 'Enviando…' : 'Enviar tarea' }}</span>
            </button>
          </div>
        </section>
      </div>
    </ng-container>
  </article>
</ng-container>

<ng-template #accordionLayout>
<div class="task-delivery"
[ngClass]="variant === 'inline' ? 'p-4 lg:p-6 m-auto w-[96%] md:w-10/12 rounded-lg bg-white shadow-[rgba(0,0,0,0.12)_0px_1px_3px,rgba(0,0,0,0.24)_0px_1px_2px]' : 'w-full'"
>
  <!-- cargando: mismos esqueletos que el modal de producción -->
  <div *ngIf="phase === 'loading'" class="flex flex-col gap-6">
    <ion-skeleton-text [animated]="true" class="w-26 h-3 rounded-lg"></ion-skeleton-text>
    <div class="flex flex-col gap-4">
      <div class="flex gap-4" *ngFor="let row of [0, 1, 2, 3]">
        <ion-skeleton-text [animated]="true" class="w-full h-3 rounded-lg"></ion-skeleton-text>
      </div>
    </div>
    <ion-skeleton-text [animated]="true" class="w-full h-11 rounded-lg"></ion-skeleton-text>
    <div class="flex gap-4">
      <ion-skeleton-text [animated]="true" class="w-9/12 h-10 rounded-lg"></ion-skeleton-text>
      <ion-skeleton-text [animated]="true" class="w-3/12 h-10 rounded-lg"></ion-skeleton-text>
    </div>
  </div>

  <!-- la tarea no pudo cargarse -->
  <div *ngIf="phase === 'error'" class="flex flex-col items-center gap-3 text-center text-gray-700 py-6">
    <p class="text-base">{{ errorMsg }}</p>
    <ion-button fill="outline" size="small" (click)="load()">Reintentar</ion-button>
  </div>

  <ng-container *ngIf="task && phase !== 'loading' && phase !== 'error'">
    <!-- encabezado -->
    <div class="flex flex-col gap-1 mb-3" *ngIf="courseName || statusLabel">
      <p *ngIf="courseName" class="text-sm text-gray-600">{{ courseName }}</p>
      <p *ngIf="statusLabel" class="text-base font-bold">
        <span class="text-secondary mr-2">Estado:</span>{{ statusLabel }}
      </p>
    </div>

    <!-- acordeón: Indicaciones / Recursos / Documentos / Comentarios -->
    <div class="flex flex-col divide-y divide-gray-200 border-y border-gray-200 mb-4">
      <div [ngClass]="openSection === 'indicaciones' ? 'rounded-lg px-1 bg-primary_super_ultra_light_variant' : ''">
        <button type="button" class="task-delivery__header" (click)="toggleSection('indicaciones')">
          <span class="text-primary text-lg font-medium">Indicaciones</span>
          <mat-icon class="mdi mdi-chevron-down text-xl text-primary transition-transform duration-200"
            [class.rotate-180]="isOpen('indicaciones')"></mat-icon>
        </button>
        <div *ngIf="isOpen('indicaciones')" class="pb-3 px-1">
          <p class="text-base font-normal whitespace-pre-line">
            {{ task.indications || 'Aquí encontrarás todos los recursos para realizar y subir tu tarea.' }}
          </p>
        </div>
      </div>

      <div [ngClass]="openSection === 'recursos' ? 'rounded-lg px-1 bg-primary_super_ultra_light_variant' : ''">
        <button type="button" class="task-delivery__header" (click)="toggleSection('recursos')">
          <span class="text-primary text-lg font-medium">Recursos de la tarea</span>
          <mat-icon class="mdi mdi-chevron-down text-xl text-primary transition-transform duration-200"
            [class.rotate-180]="isOpen('recursos')"></mat-icon>
        </button>
        <div *ngIf="isOpen('recursos')" class="pb-3 px-1">
          <ul *ngIf="task.project_resources?.length; else noTaskResources" class="flex flex-col">
            <li *ngFor="let resource of task.project_resources" class="flex items-center justify-between gap-3 py-2">
              <p class="text-base break-words min-w-0">{{ resource.title }}</p>
              <button type="button" class="shrink-0 text-primary" (click)="confirmDownload(resource)"
                [attr.aria-label]="'Descargar ' + resource.title">
                <mat-icon class="mdi mdi-download text-2xl"></mat-icon>
              </button>
            </li>
          </ul>
          <ng-template #noTaskResources>
            <p class="text-base text-gray-600 py-2">Esta sección no contiene recursos asignados.</p>
          </ng-template>
        </div>
      </div>

      <div [ngClass]="openSection === 'documentos' ? 'rounded-lg px-1 bg-primary_super_ultra_light_variant' : ''">
        <button type="button" class="task-delivery__header" (click)="toggleSection('documentos')">
          <span class="text-primary text-lg font-medium">Documentos cargados</span>
          <mat-icon class="mdi mdi-chevron-down text-xl text-primary transition-transform duration-200"
            [class.rotate-180]="isOpen('documentos')"></mat-icon>
        </button>
        <div *ngIf="isOpen('documentos')" class="pb-3 px-1">
          <ul *ngIf="userResources.length; else noUploadedTask" class="flex flex-col">
            <li *ngFor="let resource of userResources; let i = index"
              class="flex items-center justify-between gap-3 py-2">
              <span class="text-base font-medium break-words min-w-0">
                {{ resourceName(resource) }} ({{ sizeMb(resource) }}MB)
              </span>
              <div class="flex items-center gap-3 shrink-0">
                <span class="rounded-lg border h-6 px-2 flex items-center gap-1 text-base"
                  [ngClass]="isStored(resource) || isSending ? 'border-primary text-primary' : 'border-secondary text-secondary'">
                  <span>{{ isStored(resource) ? 'Cargado' : isSending ? 'Cargando' : 'Da clic en enviar' }}</span>
                  <mat-icon *ngIf="isStored(resource)" class="mdi mdi-check-bold text-base"></mat-icon>
                </span>
                <button *ngIf="!isDelivered" type="button" class="text-primary text-3xl disabled:opacity-50"
                  [disabled]="isSending" (click)="removeResource(i)" aria-label="Quitar archivo">
                  <mat-icon class="mdi mdi-delete"></mat-icon>
                </button>
              </div>
            </li>
          </ul>
          <ng-template #noUploadedTask>
            <p class="text-base text-gray-600 py-2">En esta sección se listarán los archivos que agregues a tu tarea.</p>
          </ng-template>
        </div>
      </div>

      <div *ngIf="task.msg_to_user">
        <button type="button" class="task-delivery__header" (click)="toggleSection('comentarios')">
          <span class="text-primary text-lg font-medium">Comentarios</span>
          <mat-icon class="mdi mdi-chevron-down text-xl text-primary transition-transform duration-200"
            [class.rotate-180]="isOpen('comentarios')"></mat-icon>
        </button>
        <div *ngIf="isOpen('comentarios')" class="pb-3 px-1">
          <p class="text-base font-normal whitespace-pre-line">{{ task.msg_to_user }}</p>
        </div>
      </div>
    </div>

    <!-- envío -->
    <div class="flex flex-col gap-3">
      <p *ngIf="errorMsg" class="text-sm text-red-600 text-center" role="alert">{{ errorMsg }}</p>
      <p *ngIf="successMsg" class="text-sm text-primary font-medium text-center" role="status">{{ successMsg }}</p>
      <p *ngIf="isDelivered && !successMsg" class="text-sm text-gray-600 text-center">Ya enviaste tu tarea.</p>

      <div class="flex items-center gap-2" *ngIf="canUpload">
        <ion-button class="w-9/12 normal-text" fill="outline" type="button" [disabled]="!canAddFile || isSending"
          (click)="fileInput.click()">
          <ion-icon name="add-circle" class="w-5 mr-1"></ion-icon>
          <span class="text-base font-semibold">Subir archivo</span>
        </ion-button>
        <input #fileInput type="file" class="hidden" [accept]="acceptTypes" (change)="onFileSelected($event)" />

        <ion-button class="w-3/12" type="button" color="primary" [disabled]="!canSend" (click)="send()">
          <span class="text-white text-base font-semibold">Enviar</span>
        </ion-button>
      </div>

      <ion-button *ngIf="!canUpload" class="w-full" type="button" color="primary" [disabled]="!canSend" (click)="send()">
        <span class="text-white text-base font-semibold">Enviar</span>
      </ion-button>
    </div>
  </ng-container>
</div>
</ng-template>
